import { Icon } from "@clarkinc/zest-design-system/ui";
import { logException } from "@wss/error-tracking";
import { useReCaptcha } from "@wss/re-captcha";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import ChatbotAnswerAndFeedback from "./ChatbotAnswerAndFeedback";
import requestAnswer from "./requestAnswer";
import { sendGtmEvent } from "~/analytics/definitions";
import spritePath from "~/common/Icons/Standard/standard-sprite.svg";
import { DocumentSummary } from "./Models";
interface ChatbotQuestionsAndAnswersProps {
  itemNumberId: number;
  itemNumber: string;
  isExpandedDescriptionTemplate: boolean;
}
const ChatbotQuestionsAndAnswers = ({
  itemNumberId,
  itemNumber,
  isExpandedDescriptionTemplate
}: ChatbotQuestionsAndAnswersProps) => {
  const reCaptcha = useReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState("");
  const [interactionKey, setInteractionKey] = useState("");
  const [feedback, setFeedback] = useState("");
  const [questionFocused, setQuestionFocused] = useState(false);
  const [relevantDocuments, setRelevantDocuments] = useState<DocumentSummary[]>([]);
  const questionInputRef = useRef<HTMLInputElement>(null);
  const questionDivRef = useRef<HTMLDivElement>(null);
  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    setButtonDisabled(false);
    setQuestion(event.currentTarget.value);
    if (questionInputRef.current) {
      questionInputRef.current.style.paddingRight = questionInputRef.current.value.length ? "7rem" : "5rem";
    }
  };
  const handleClear = () => {
    setButtonDisabled(false);
    setQuestion("");
    if (questionInputRef.current) {
      questionInputRef.current.focus();
    }
  };
  useEffect(() => {
    if (questionDivRef.current) {
      if (isLoading) {
        questionDivRef.current.style.height = "300px";
      } else if (answer || error) {
        questionDivRef.current.style.minHeight = "300px";
        questionDivRef.current.style.height = "auto";
      } else {
        questionDivRef.current.style.height = "56px";
      }
    }
  });
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setAnswer("");
    setInteractionKey("");
    setFeedback("");
    setIsLoading(true);
    setButtonDisabled(true);
    const recaptchaToken = await reCaptcha.execute();
    if (recaptchaToken.isError) {
      setError("Invalid ReCaptcha");
      setIsLoading(false);
      setButtonDisabled(false);
      return;
    }
    const response = await requestAnswer({
      itemNumberId,
      question,
      recaptchaToken: recaptchaToken.value
    });
    if (response.isError) {
      // avoid logging recaptcha errors to Sentry, these are logged to Graylog
      if (!String(response.error.cause).includes("Invalid Recaptcha")) {
        await logException(response.error);
      }
      setError(response.error.message);
      setIsLoading(false);
      setButtonDisabled(false);
      return;
    }
    setAnswer(response.value.answer.unanswered ? "I do not have the information to answer that at this time. Please rephrase your question or visit the help center if you need more assistance." : response.value.answer.response);
    setInteractionKey(response.value.interactionKey);
    setError("");
    setIsLoading(false);
    setRelevantDocuments(response.value.answer?.relevantDocuments ?? []);
    sendGtmEvent({
      event: "chatbot-interaction",
      eventCategory: "PDP",
      eventAction: "PDP Chatbot Interaction"
    });
  };
  return <div className="px-6 pt-6 pb-8" data-sentry-component="ChatbotQuestionsAndAnswers" data-sentry-source-file="ChatbotQuestionsAndAnswers.tsx">
      <h2 className={`font-semibold pb-4 m-0 text-2xl ${isExpandedDescriptionTemplate ? "md:text-3xl" : ""}`}>
        Questions &amp; Answers
      </h2>
      <div className="rounded-xl bg-[#DCEFD9] pb-1.5">
        <h3 className="grid md:block text-sm font-bold m-0 py-3.5 px-5 text-shadow-none">
          Looking For Specific Info?{" "}
          <span className="font-normal">
            Get quick answers to product questions with our AI-powered search.
          </span>
        </h3>
        <div ref={questionDivRef} className="rounded-lg bg-white mx-1.5 border-solid border border-gray-600 focus:shadow-[0px_0px_0px_2px_#9DC68A] transition-height duration-500">
          <form id="chatbotForm" onSubmit={handleSubmit} aria-busy={isLoading ? "true" : "false"}>
            <legend className="sr-only">Ask about this product</legend>
            <div className={`relative flex flex-row ${questionFocused ? "shadow-[0px_0px_0px_2px_#9DC68A]" : ""} ${questionFocused && (isLoading || answer || error) ? "rounded-t-lg rounded-b-none" : "rounded-lg"}`}>
              <Icon name="chatbot-question" className="absolute w-7 h-7 z-5 pt-4 pl-3.5" spriteSheetHref={spritePath} data-sentry-element="Icon" data-sentry-source-file="ChatbotQuestionsAndAnswers.tsx" />
              <input name="question" ref={questionInputRef} data-testid="pdp-chatbot-question-input" placeholder="Ask about this product" id="chatbot-input" type="text" className={`w-full pl-12 py-0 mb-0 h-14 font-semibold xlarge text-base border-gray-400 pr-20 text-ellipsis shadow-none ${isLoading || answer || error ? "border-b border-b-solid border-t-0 border-x-0 rounded-t-lg rounded-b-none drop-shadow" : "border-none rounded-lg"}`} value={question} onChange={handleChange} onFocus={() => {
              setQuestionFocused(true);
            }} onBlur={() => {
              setQuestionFocused(false);
            }} minLength={4} required aria-describedby="chatbot-description" style={{
              fontFamily: "inherit"
            }} />
              {question && <button type="button" className="absolute bg-transparent border-0 pt-[18px] right-20 p-0 mr-1.5" onClick={handleClear} data-testid="pdp-chatbot-clear-question">
                  <Icon name="ri-close-circle-line" className="fill-gray-600 w-5 h-5" />
                </button>}
              <button data-testid="pdp-chatbot-ask-button" className={`ask-button absolute whitespace-nowrap border-none top-2 right-2 w-16 h-10 rounded-md font-semibold text-base px-3.5 text-white chat-ask-button-gradient chat-ask-button-hover ${buttonDisabled ? "bg-green-300 pointer-events-none opacity-50" : ""}`} type="submit" disabled={buttonDisabled} style={{
              fontFamily: "inherit"
            }}>
                Ask
              </button>
            </div>
          </form>
          {isLoading && <div className="animate-pulse flex flex-col pt-6" data-testid="pdp-chatbot-loading-flasher">
              <div className="rounded-md w-3 h-3 ml-5 chat-ask-button-gradient" />
            </div>}
          {answer && <ChatbotAnswerAndFeedback answer={answer} feedback={feedback} interactionKey={interactionKey} itemNumber={itemNumber} relevantDocuments={relevantDocuments} setFeedback={setFeedback} setError={setError} />}
          {error && <div className="text-xs font-normal text-red-400 pt-1" data-testid="pdp-chatbot-error-message">
              {error}
            </div>}
        </div>
      </div>
    </div>;
};
export default ChatbotQuestionsAndAnswers;